/**
 * A native marketing connection (SCRUM-2287 — analytics without Windsor):
 * Google Analytics 4, Google Search Console, Google Ads or Meta Ads,
 * connected through our own OAuth apps. Connect → pick the account → test,
 * change or disconnect; Google Ads adds a Diagnose panel for Prospera super
 * admins (the OAuth client's Cloud project, and a live API probe).
 *
 * Restored from before the Windsor.ai connector replaced it (bade320f7);
 * the backend OAuth endpoints never went away.
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3, Search, Megaphone, Globe, CheckCircle, AlertTriangle, Trash2, Plug, Link as LinkIcon,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  deleteMarketingIntegration,
  testMarketingIntegration,
  startOAuth,
  listPickableAccounts,
  selectAccount,
  getGoogleAdsStatus,
  diagnoseGoogleAds,
  PROVIDER_LABELS,
  PROVIDER_HELP,
  PICK_LABEL,
} from '@/services/marketingIntegrations.service';
import { SEARCH_CONSOLE_VISIBLE } from '@/pages/marketing/ads/searchConsoleVisibility';
import type {
  GoogleAdsStatus, GoogleAdsDiagnose, MarketingIntegrationRow,
} from '@/services/marketingIntegrations.service';

/** The four providers connected through our own OAuth apps. */
export const NATIVE_PROVIDERS = ['google_analytics', 'google_search_console', 'google_ads', 'meta_ads'] as const;
export type NativeProvider = (typeof NATIVE_PROVIDERS)[number];

/** The ones offered to this account: Meta Ads only once its Marketing opt-in is on. */
export function offeredNativeProviders(metaAdsEnabled: boolean): NativeProvider[] {
  return NATIVE_PROVIDERS.filter((p) =>
    (p !== 'meta_ads' || metaAdsEnabled) && (p !== 'google_search_console' || SEARCH_CONSOLE_VISIBLE));
}

const PROVIDER_ICON: Record<NativeProvider, React.ComponentType<{ className?: string }>> = {
  google_analytics: BarChart3,
  google_ads: Search,
  meta_ads: Megaphone,
  google_search_console: Globe,
};

const PROVIDER_TONE: Record<NativeProvider, 'amber' | 'blue' | 'sky' | 'emerald'> = {
  google_analytics: 'amber',
  google_ads: 'blue',
  meta_ads: 'sky',
  google_search_console: 'emerald',
};

function describeApiError(e: any): string {
  return e?.response?.data?.error?.message ?? e?.message ?? 'Request failed';
}

function StatusPill({ row }: { row: MarketingIntegrationRow }) {
  if (row.connectionStatus === 'not_connected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
        <Plug className="h-3 w-3" /> Not connected
      </span>
    );
  }
  if (row.connectionStatus === 'needs_selection') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700">
        <AlertTriangle className="h-3 w-3" /> Pick an account
      </span>
    );
  }
  if (row.status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700">
        <AlertTriangle className="h-3 w-3" /> Error
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700">
      <CheckCircle className="h-3 w-3" /> Connected
    </span>
  );
}

/**
 * Locked (plan 2026-10-10-001 U7: R8, R18): the connection state and linked
 * account show, with no Connect, account picker, Change, Test Connection (it
 * saves the result on the row, so it writes) or Disconnect. Nothing says why.
 * Diagnose stays for super admins: it only reads.
 */
export function ProviderCard({ row }: { row: MarketingIntegrationRow & { provider: NativeProvider } }) {
  const locked = useAdminPageLevel() === 'locked';
  const provider = row.provider;
  const Icon = PROVIDER_ICON[provider];
  const tone = PROVIDER_TONE[provider];
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Google Ads diagnostic: the OAuth client's Cloud project and a live API
  // probe. A developer tool, so Prospera super admins only; clinic staff get
  // Test Connection and the dashboards' own error messages.
  const { isSuperAdmin } = useAuth();
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);

  // Pull the picker list only when the OAuth token exists but no entity has
  // been selected yet (or when the user explicitly hits "Change account").
  const [pickerOpen, setPickerOpen] = useState(false);
  const showPicker = !locked && (pickerOpen || row.connectionStatus === 'needs_selection');

  const { data: pickList, isFetching: pickerLoading, error: pickerError } = useQuery({
    queryKey: ['admin', 'marketing-integrations', provider, 'accounts'],
    queryFn: () => listPickableAccounts(provider),
    enabled: showPicker,
    retry: false,
  });

  const connectMut = useMutation({
    mutationFn: () => startOAuth(provider),
    onSuccess: (res) => {
      // Send the user to the provider's authorize page. Replace, not new tab —
      // the callback will redirect us back to this same admin page.
      window.location.href = res.authorizeUrl;
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const selectMut = useMutation({
    mutationFn: ({ id, label, loginCustomerId }: { id: string; label?: string; loginCustomerId?: string | null }) =>
      selectAccount(provider, id, label, loginCustomerId),
    onSuccess: () => {
      toast.success(`${PROVIDER_LABELS[provider]} linked`);
      setPickerOpen(false);
      qc.invalidateQueries({ queryKey: ['admin', 'marketing-integrations'] });
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const testMut = useMutation({
    mutationFn: () => testMarketingIntegration(provider),
    onSuccess: (res) => toast.success(res?.note ?? 'Test passed'),
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteMarketingIntegration(provider),
    onSuccess: () => {
      toast.success(`${PROVIDER_LABELS[provider]} disconnected`);
      qc.invalidateQueries({ queryKey: ['admin', 'marketing-integrations'] });
      setConfirmDelete(false);
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const accountOptions = useMemo(
    () => (pickList?.accounts ?? []).map((a) => ({ value: a.id, label: a.label })),
    [pickList],
  );

  return (
    <Card className="space-y-0">
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            'h-9 w-9 rounded-lg grid place-items-center',
            tone === 'amber' && 'bg-amber-50 text-amber-600',
            tone === 'blue' && 'bg-blue-50 text-blue-600',
            tone === 'sky' && 'bg-sky-50 text-sky-600',
            tone === 'emerald' && 'bg-emerald-50 text-emerald-600',
          )}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold text-slate-800">{PROVIDER_LABELS[provider]}</div>
            <div className="mt-0.5"><StatusPill row={row} /></div>
          </div>
        </div>
        {!locked && row.connectionStatus !== 'not_connected' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmDelete(true)}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Disconnect
          </Button>
        )}
      </CardHeader>

      <CardBody className="space-y-4">
        <p className="text-sm text-slate-500">{PROVIDER_HELP[provider]}</p>

        {row.lastError && row.connectionStatus !== 'not_connected' && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <strong>Last error:</strong> {row.lastError}
          </div>
        )}

        {/* State 1 — not connected: OAuth button */}
        {!locked && row.connectionStatus === 'not_connected' && (
          <Button onClick={() => connectMut.mutate()} disabled={connectMut.isPending}>
            <LinkIcon className="h-4 w-4 mr-2" />
            {connectMut.isPending ? 'Redirecting…' : `Connect ${PROVIDER_LABELS[provider]}`}
          </Button>
        )}

        {/* State 2 — picker needed (or user opened it again) */}
        {showPicker && row.connectionStatus !== 'not_connected' && (
          <div className="space-y-2">
            {row.connectedAs && (
              <div className="text-xs text-slate-500">Connected as <span className="font-medium text-slate-700">{row.connectedAs}</span></div>
            )}
            {pickerLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner className="h-4 w-4" /> Loading accounts…</div>
            ) : pickerError ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                Failed to load accounts: {describeApiError(pickerError)}
              </div>
            ) : (
              <>
                <Select
                  options={accountOptions}
                  value=""
                  onChange={(id) => {
                    // Look up the full account (not the trimmed option) so we
                    // carry loginCustomerId — the MCC needed for Google Ads.
                    const acct = (pickList?.accounts ?? []).find((a) => a.id === id);
                    if (acct) {
                      selectMut.mutate({ id: acct.id, label: acct.label, loginCustomerId: acct.loginCustomerId ?? null });
                    }
                  }}
                  placeholder={accountOptions.length ? PICK_LABEL[provider] : 'No accounts available'}
                  disabled={selectMut.isPending || accountOptions.length === 0}
                />
                {accountOptions.length === 0 && (
                  <div className="text-xs text-slate-500">
                    Your account doesn't have access to any {
                      provider === 'google_analytics' ? 'GA4 properties' :
                      provider === 'google_ads' ? 'Ads customers' :
                      provider === 'google_search_console' ? 'verified Search Console sites' :
                      'ad accounts'
                    } we can read. Make sure you signed in with the right user.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* State 3 — fully connected: show selection + actions */}
        {row.connectionStatus === 'connected' && !pickerOpen && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400">Linked to</div>
              <div className="font-medium">{row.selected?.label ?? row.selected?.id}</div>
              {row.connectedAs && <div className="text-xs text-slate-500 mt-0.5">via {row.connectedAs}</div>}
            </div>
            {!locked && (
              <Button variant="ghost" size="sm" onClick={() => setPickerOpen(true)}>
                Change
              </Button>
            )}
          </div>
        )}

        {row.connectionStatus !== 'not_connected' && (
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            {!locked && (
              <Button
                variant="ghost"
                onClick={() => testMut.mutate()}
                disabled={testMut.isPending || row.connectionStatus !== 'connected'}
              >
                {testMut.isPending ? <Spinner className="h-4 w-4 mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Test Connection
              </Button>
            )}
            {provider === 'google_ads' && isSuperAdmin && (
              <Button variant="ghost" onClick={() => setDiagnoseOpen(true)}>
                Diagnose
              </Button>
            )}
            {row.lastSyncAt && (
              <span className="text-xs text-slate-400 ml-auto">
                Last verified: {new Date(row.lastSyncAt).toLocaleString()}
              </span>
            )}
          </div>
        )}
      </CardBody>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={`Disconnect ${PROVIDER_LABELS[provider]}?`}
        message="Stored credentials will be deleted. You can reconnect anytime."
        confirmLabel="Disconnect"
        variant="danger"
        onConfirm={() => deleteMut.mutateAsync().then(() => undefined)}
        onClose={() => setConfirmDelete(false)}
      />

      {provider === 'google_ads' && isSuperAdmin && (
        <GoogleAdsDiagnoseModal isOpen={diagnoseOpen} onClose={() => setDiagnoseOpen(false)} />
      )}
    </Card>
  );
}

function GoogleAdsDiagnoseModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  // Rendered for super admins only (ProviderCard). The OAuth client config
  // (fingerprints, Cloud project) comes from an operator endpoint behind
  // EnsureSuperAdmin; the live probe runs against this practice's connection.
  const status = useQuery({
    queryKey: ['google-ads-status'],
    queryFn: getGoogleAdsStatus,
    enabled: isOpen,
    staleTime: 0,
    retry: false,
  });
  const probe = useQuery({
    queryKey: ['google-ads-diagnose'],
    queryFn: diagnoseGoogleAds,
    enabled: isOpen,
    staleTime: 0,
    retry: false,
  });

  const reRun = () => {
    void status.refetch();
    void probe.refetch();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Google Ads — Diagnose">
      <div className="space-y-3 text-sm">
        {status.data && <ConfigSection data={status.data} />}
        {status.error && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            The OAuth client configuration could not be read. The live probe below checks this
            practice&apos;s own connection.
          </div>
        )}
        <ProbeSection q={probe} />
        <div className="flex justify-end">
          <Button variant="ghost" onClick={reRun} disabled={status.isFetching || probe.isFetching}>
            {status.isFetching || probe.isFetching ? 'Running…' : 'Re-run live probe'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ConfigSection({ data }: { data: GoogleAdsStatus }) {
  const projectMatch = data.clientId.projectNumber;
  return (
    <>
      <div className={cn(
        'rounded-md border px-3 py-2',
        data.configured
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : 'border-amber-200 bg-amber-50 text-amber-900',
      )}>
        <div className="font-semibold">{data.configured ? 'Env config complete' : 'Env config incomplete'}</div>
        <div className="mt-0.5 text-xs">{data.hint}</div>
      </div>
      {projectMatch && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
          <div className="text-xs uppercase tracking-wide text-blue-700 font-semibold">OAuth client GCP project</div>
          <div className="mt-0.5 font-mono text-base text-blue-900">{projectMatch}</div>
          <div className="mt-1 text-[11px] text-blue-800">
            Google Ads API access is granted to this project in Google Cloud Console → Google Ads API →
            Access levels. A developer token is no longer required.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {/* A fingerprint, never any part of the secret: compare it across deploys. */}
        <Flag label="Client ID" ok={data.clientId.configured} extra={data.clientId.fingerprint ?? undefined} />
        <Flag label="Client Secret" ok={data.clientSecret.configured} extra={data.clientSecret.fingerprint ?? undefined} />
        {/* Optional since Google dropped developer tokens: never shown as missing. */}
        <Flag
          label="Developer Token"
          ok
          okText={data.developerToken.configured ? 'Configured' : 'Not required'}
          extra={data.developerToken.fingerprint ?? undefined}
        />
      </div>
    </>
  );
}

function ProbeSection({ q }: { q: { data: GoogleAdsDiagnose | undefined; isLoading: boolean; error: unknown } }) {
  if (q.isLoading) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Live probe</div>
        <div className="flex items-center gap-2 text-xs text-slate-600"><Spinner className="h-3.5 w-3.5" /> Running…</div>
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        Could not run the live probe: {String((q.error as any)?.message ?? 'unknown error')}
      </div>
    );
  }
  if (!q.data) return null;
  const d = q.data;

  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Live probe</div>

      <div className="grid grid-cols-1 gap-2 mb-2 sm:grid-cols-2">
        <Flag label="Integration row" ok={d.account.hasRow} />
        <Flag label="Refresh token saved" ok={d.account.hasRefreshToken} />
      </div>

      {d.account.customerId && (
        <div className="text-[11px] text-slate-600 mb-2">
          <div><span className="font-semibold">customer:</span> <code>{d.account.customerId}</code></div>
          {d.account.loginCustomerId && (
            <div><span className="font-semibold">login-customer-id:</span> <code>{d.account.loginCustomerId}</code></div>
          )}
        </div>
      )}

      {d.probe ? (
        <>
          {/* Step 1: access token */}
          <div className={cn(
            'rounded-md border px-2 py-1.5 mb-1.5 text-xs',
            d.probe.accessToken.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800',
          )}>
            <div className="font-semibold">
              {d.probe.accessToken.ok ? '✓ OAuth refresh succeeded' : '✗ OAuth refresh failed'}
            </div>
            {d.probe.accessToken.error && <div className="mt-0.5 font-mono text-[10px] break-all">{d.probe.accessToken.error}</div>}
          </div>

          {/* Step 2: listAccessibleCustomers */}
          {d.probe.listAccessibleCustomers && (
            <div className={cn(
              'rounded-md border px-2 py-1.5 text-xs',
              d.probe.listAccessibleCustomers.ok
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-800',
            )}>
              <div className="font-semibold">
                {d.probe.listAccessibleCustomers.ok
                  ? `✓ GET customers:listAccessibleCustomers → HTTP ${d.probe.listAccessibleCustomers.status}`
                  : `✗ GET customers:listAccessibleCustomers → HTTP ${d.probe.listAccessibleCustomers.status}`}
              </div>
              {d.probe.listAccessibleCustomers.error && (
                <div className="mt-0.5 font-mono text-[10px] break-all">
                  {d.probe.listAccessibleCustomers.error}
                </div>
              )}
              {d.probe.listAccessibleCustomers.body && !d.probe.listAccessibleCustomers.ok && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[10px] underline">Raw response</summary>
                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-white/60 p-2 text-[10px] text-slate-700">
                    {d.probe.listAccessibleCustomers.body}
                  </pre>
                </details>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-slate-500">No probe run — connect the account first via OAuth.</div>
      )}

      {d.hint && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
          {d.hint}
        </div>
      )}
    </div>
  );
}

function Flag({ label, ok, okText = 'Configured', extra }: { label: string; ok: boolean; okText?: string; extra?: string }) {
  return (
    <div className={cn(
      'rounded-md border px-2 py-1.5',
      ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50',
    )}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={cn('text-xs font-medium', ok ? 'text-emerald-700' : 'text-amber-800')}>
        {ok ? okText : 'Missing'}
      </div>
      {extra && <div className="text-[10px] text-slate-500 mt-0.5">{extra}</div>}
    </div>
  );
}
